import React from "react";

/**
 * V9.44.3 — ErrorBoundary for chat-bubble side-cards (reminder lookup,
 * created chips, task chips, live-data badges, step guides).
 *
 * BEFORE this existed, a throw inside any of those components unmounted
 * the entire Chat route and produced a black page. Now the boundary
 * catches the throw, swallows it into a tiny inline pill, and leaves
 * the rest of the conversation alive so the user can keep chatting.
 *
 * Usage:
 *   <ChipErrorBoundary label="reminder lookup">
 *     <ReminderLookupChip lookup={lookup} />
 *   </ChipErrorBoundary>
 */
export default class ChipErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(err) {
    return {
      hasError: true,
      message: (err && (err.message || String(err))) || "render error",
    };
  }

  componentDidCatch(err, info) {
    // Best-effort console log — survives even if `console` is shimmed.
    try {
      // eslint-disable-next-line no-console
      console.error(
        `[ChipErrorBoundary] ${this.props.label || "chip"} crashed:`,
        err,
        info?.componentStack,
      );
    } catch (_) {
      /* noop */
    }
    // V9.44.4 — Auto-report to backend so the operator can inspect
    // the exact prop payload + component stack without needing the
    // user to open DevTools. Fire-and-forget; never block render.
    try {
      const url =
        (typeof process !== "undefined" &&
          process.env &&
          process.env.REACT_APP_BACKEND_URL) ||
        "";
      // Pull the FIRST child's props so we can see what payload
      // crashed the chip. React stores them on the fiber, accessible
      // via children prop.
      let childProps = null;
      try {
        const children = this.props.children;
        if (children && children.props) {
          childProps = JSON.parse(
            JSON.stringify(children.props, (_k, v) =>
              // Truncate huge strings; drop functions; preserve plain objects.
              typeof v === "function" ? undefined :
              typeof v === "string" && v.length > 500 ? v.slice(0, 500) + "…" :
              v,
            ),
          );
        }
      } catch (_) { /* serialization can fail on cyclic refs */ }

      const body = JSON.stringify({
        label: this.props.label || "chip",
        message: (err && (err.message || String(err))) || "",
        stack: (err && err.stack) || "",
        component_stack: (info && info.componentStack) || "",
        props: childProps,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
      fetch(`${url}/api/debug/client-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        credentials: "include",
        keepalive: true,
      }).catch(() => { /* swallow */ });
    } catch (_) {
      /* noop */
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10.5px] bg-rose-500/8 border border-rose-400/20 text-rose-200"
          data-testid={`chip-error-${this.props.label || "chip"}`}
          title={this.state.message}
        >
          <span aria-hidden="true">⚠</span>
          {(this.props.label || "chip")} couldn't render
        </div>
      );
    }
    return this.props.children;
  }
}
